'use client';

import { useRouter } from '@bprogress/next/app';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CartItemFragment,
  CartFragment,
  ProductFragment,
  CartItemCheckoutFragment,
  SimpleProductFragment,
  addProductsToWishlist,
  getFragmentData,
  ProductFragmentDoc
} from '@/fo/api/magento';
import { FeatureCheckoutSummaryCompact } from '@/fo/feature/checkout/summary/compact';
import { FeatureProductTileSecondary } from '@/fo/feature/product/tile/secondary';
import { cartActions } from '@/fo/store/product';
import { Title, TitleGroup, UiCartLayoutPage } from '@/fo/ui/cart/layout/page';
import { UiDeliveryInformerBase } from '@/fo/ui/delivery/informer/base';
import { UiKitLoaderBase } from '@/fo/ui/kit/loader/base';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { toastBase } from '@/fo/ui/kit/toast/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';
import {
  // MappedProduct,
  ProductsMindbox,
  utilMapProductData
} from '@/fo/util/map-product-data';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { AuthModal } from '../components/AuthModal/AuthModal';
import { ClearCartModal } from '../components/ClearCartModal/ClearCartModal';
import { EmptyCart } from '../components/EmptyCart';
import { StickyMobileButton } from '../components/StickyMobileButton/StickyMobileButton';

import { styles } from './FeatureCartPageBase.styles';

export interface FeatureCartPageBaseProps {
  children?: React.ReactNode;
  cart?: CartFragment | null;
  items?: CartItemFragment[];
  hideGuestCheckout?: boolean;
  isAuthorized?: boolean;
  isCartLoading?: boolean;
  mindboxProducts?: ProductsMindbox | null;
}
declare global {
  interface Window {
    upravel?: (
      id: string,
      goal: string,
      action: string,
      parameters: string,
      cnl: number
    ) => void;
  }
}
export function FeatureCartPageBase({
  children,
  cart,
  items = [],
  isAuthorized = false,
  hideGuestCheckout = false,
  isCartLoading = false,
  mindboxProducts
}: FeatureCartPageBaseProps) {
  const t = useTranslations();
  const router = useRouter();
  const slots = styles();
  const cartWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState<null | 'unavailable'>(null);

  const [isClearCartLoading, setIsClearCartLoading] = useState(false);
  const [clearCartConfirm, setClearCartConfirm] = useState(false);
  const [isStickyButtonVisible, setIsStickyButtonVisible] = useState(false);

  const handleStickyButtonVisibilityChange = useCallback((visible: boolean) => {
    setIsStickyButtonVisible(visible);
  }, []);

  const normalizedItems = items.filter((item) => (item?.quantity ?? 1) > 0);

  const mapCartItem = (item: CartItemFragment) => {
    const base = utilMapProductData(
      item.product as ProductFragment,
      t,
      mindboxProducts
    );
    const discountsSum = item.prices?.total_item_discount?.value ?? 0;
    const rowTotalIncl = item.prices?.row_total_including_tax?.value ?? null;
    const finalRow = rowTotalIncl !== null ? rowTotalIncl - discountsSum : null;
    const quantity = item.quantity ?? 1;
    const finalUnit =
      finalRow !== null && quantity > 0 ? finalRow / quantity : null;

    return {
      ...base,
      basePrice: rowTotalIncl,
      finalPrice: finalRow,
      discountTotal: discountsSum,
      itemPrice: finalUnit,
      mindboxBonuses: item?.mindbox_earned_bonus_amount ?? 0
    };
  };

  const mappedProducts = normalizedItems
    .filter((item) => !item.is_cart_rule_gift)
    .map((item) => mapCartItem(item));

  const userChoiceCartItemsMap = new Map<string, string>();
  normalizedItems
    .filter((item) => item.is_cart_rule_gift && item.user_choice_rule_id)
    .forEach((item) => {
      const product = item.product as ProductFragment;
      if (product?.sku && item.id) {
        userChoiceCartItemsMap.set(product.sku, item.id);
      }
    });

  const products = mappedProducts.filter(
    (item) => item.stockStatus === 'IN_STOCK'
  );
  const notAvailableProducts = mappedProducts.filter(
    (item) => item.stockStatus !== 'IN_STOCK'
  );
  const hasOutOfStockProducts = notAvailableProducts.length > 0;

  const discounts = cart?.prices?.discounts ?? [];
  const totalDiscounts = discounts.reduce(
    (acc, discount) => acc + (discount?.amount?.value ?? 0),
    0
  );
  const subtotalPrice = cart?.prices?.subtotal_excluding_tax?.value ?? 0;
  const subtotalPriceWithoutDiscounts = subtotalPrice - totalDiscounts;
  const freeShippingRemaining = cart?.free_shipping_remaining;
  const isFreeShipping = !freeShippingRemaining;
  const currency = cart?.prices?.subtotal_excluding_tax?.currency ?? undefined;
  useEffect(() => {
    if (hasOutOfStockProducts) {
      // без задержки тост не отображается
      setTimeout(() => {
        toastBase(t('form.message.unavailableProducts'), { status: 'error' });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOutOfStockProducts]);

  useEffect(() => {
    if (!isAuthorized) return;
    setIsModalOpen(false);
  }, [isAuthorized]);

  const onMainButtonClick = () => {
    if (hasOutOfStockProducts) {
      document.getElementById('unavailableProducts')?.scrollIntoView({
        behavior: 'smooth'
      });
      toastBase(t('form.message.unavailableProducts'), { status: 'error' });
      return;
    }

    const probes = document.getElementById('cart-probes');
    if (probes) {
      utilScrollToElement(probes);
    }

    window.upravel?.(
      '29300000561',
      'any',
      'order',
      `client_id_null_url_/checkout`,
      1
    );

    if (!isAuthorized) {
      setIsModalOpen(true);
    } else {
      utilEventCartModal.close();
      router.push('/checkout');
      // router.refresh();
    }
  };

  const onClearUnavailableProducts = async () => {
    setLoading('unavailable');
    const cartIds = items
      .filter(
        (item) =>
          (item.product as SimpleProductFragment)?.stockStatus ===
          'OUT_OF_STOCK'
      )
      .map((item) => ({
        id: item.id,
        product: utilMapProductData(
          item.product as ProductFragment,
          t,
          mindboxProducts
        )
      }))
      .filter((id) => id !== undefined);
    await cartActions.removeAllCartItem(cartIds);
    // router.refresh();
    utilEventCartModal.mutate();
    setLoading(null);
  };

  const onClearCartClick = async () => {
    setClearCartConfirm(true);
  };

  const clearCart = async () => {
    setIsClearCartLoading(true);

    const cartIds = items
      .map((product) => ({
        id: product.id,
        product: utilMapProductData(
          product.product as ProductFragment,
          t,
          mindboxProducts
        )
      }))
      .filter((id) => id !== undefined);
    await cartActions.removeAllCartItem(cartIds);
    // router.refresh();
    utilEventCartModal.mutate();
    utilEventCartModal.close();

    setClearCartConfirm(false);
    setIsClearCartLoading(false);
  };

  const cartToWishlist = async () => {
    if (!isAuthorized) {
      utilEventAuthModal.open('cart');
      return;
    }

    setIsClearCartLoading(true);

    const cartIds = items
      .map((product) => ({
        id: product.id,
        product: utilMapProductData(
          product.product as ProductFragment,
          t,
          mindboxProducts
        )
      }))
      .filter((id) => id !== undefined);

    const productIds = items
      .map((product) => {
        const productItem = getFragmentData(
          ProductFragmentDoc,
          product.product
        );
        return productItem?.sku;
      })
      .filter((sku) => sku !== undefined && sku !== null);

    await cartActions.removeAllCartItem(cartIds);
    const response = await addProductsToWishlist(productIds);

    if (response.errorMessage) {
      toastBase(response.errorMessage, { status: 'error' });
    }

    // router.refresh();
    utilEventCartModal.mutate();

    setClearCartConfirm(false);
    setIsClearCartLoading(false);
  };

  if (isCartLoading) {
    return (
      <div className="container flex h-full w-full items-center justify-center max-md:min-h-[90vh]">
        <UiKitLoaderBase show />
        {children}
      </div>
    );
  }

  if (!cart || (products.length === 0 && notAvailableProducts.length === 0)) {
    return (
      <div className="container flex h-full w-full flex-col items-center justify-center max-md:min-h-[90vh]">
        <EmptyCart />
        {children}
      </div>
    );
  }

  const delivery = isFreeShipping ? null : (
    <UiDeliveryInformerBase
      subtotalPrice={subtotalPriceWithoutDiscounts}
      leftAmount={freeShippingRemaining}
      currency={currency}
    />
  );

  return (
    <div
      className="relative container flex flex-col p-0"
      ref={cartWrapperRef}
    >
      <UiCartLayoutPage
        slots={{
          title: (
            <Title
              onClearAll={onClearCartClick}
              isFreeShipping={isFreeShipping}
            />
          ),
          delivery: delivery,
          products: (
            <div className="flex w-full flex-col gap-15">
              {products.length > 0 && (
                <div className="flex flex-col gap-3">
                  {products.map((product, index) => (
                    <FeatureProductTileSecondary
                      key={`${product.sku}-${index}`}
                      isAuthorized={isAuthorized}
                      className={slots.product()}
                      data={product}
                      type="base"
                      isCart
                      showOldPrice
                    />
                  ))}
                </div>
              )}
              {notAvailableProducts.length > 0 && (
                <div className="relative z-0">
                  <TitleGroup
                    id="unavailableProducts"
                    type="unavailable"
                    isSupCount={false}
                    count={notAvailableProducts.length}
                    onClearAll={onClearUnavailableProducts}
                  />
                  <div className="flex flex-col gap-6">
                    {notAvailableProducts.map((product, index) => (
                      <FeatureProductTileSecondary
                        key={`${product.sku}-${index}`}
                        isAuthorized={isAuthorized}
                        className={slots.product()}
                        data={product}
                        type="unavailable"
                        isCart
                      />
                    ))}
                  </div>
                  <UiKitLoaderBase show={loading === 'unavailable'} />
                </div>
              )}
            </div>
          ),
          summary: (
            <FeatureCheckoutSummaryCompact
              data={cart}
              items={normalizedItems as CartItemCheckoutFragment[]}
              isAuthorized={isAuthorized}
              onMainButtonClick={onMainButtonClick}
              isCart
              buttonText={t('orderButton')}
              onStickyButtonVisibilityChange={
                handleStickyButtonVisibilityChange
              }
            />
          ),
          stickyButton: (
            <StickyMobileButton
              totals={
                <UiKitPriceBase
                  value={subtotalPriceWithoutDiscounts}
                  currency={currency}
                  className={slots.stickyTotals()}
                />
              }
              onClick={onMainButtonClick}
              mainButtonText={t('orderButton')}
              visible={isStickyButtonVisible}
            />
          )
        }}
      />
      <AuthModal
        hideGuestCheckout={hideGuestCheckout}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
      <ClearCartModal
        open={clearCartConfirm}
        onOpenChange={setClearCartConfirm}
        onConfirm={clearCart}
        onCartToWishlist={cartToWishlist}
        isLoading={isClearCartLoading}
      />
      {children}
    </div>
  );
}
